<template>
  <div class="package-management-edit-page w-100">
    <!-- Header Section -->
    <div class="w-100 px-8 py-4 bg-white border-bottom">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div>
            <h5 class="mb-0 font-weight-bold text-dark">
              {{ isCreateForm ? 'Tạo gói dịch vụ mới' : 'Chỉnh sửa gói dịch vụ' }}
            </h5>
            <small class="text-muted" v-if="form.name">{{ form.name }}</small>
          </div>
        </div>
        <div>
          <b-button variant="secondary" class="mr-2" @click="goBack">
            Hủy
          </b-button>
          <loading-button
            :handler="handleValidate"
            class="btn btn-success"
          >
            {{ isCreateForm ? 'Thêm mới' : 'Cập nhật' }}
          </loading-button>
        </div>
      </div>
    </div>

    <!-- Form Content -->
    <b-overlay :show="loading">
      <div class="container-fluid py-6">
        <div class="row justify-content-center">
          <div class="col-lg-9">
            <package-management-form
              ref="packageForm"
              v-model="form"
            />
          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal
      id="modal-apply-bundle-changes"
      size="lg"
      centered
      hide-footer
      title="Áp dụng thay đổi bundle"
      :no-close-on-backdrop="true"
      :no-close-on-esc="syncState.running"
      :hide-header-close="syncState.running"
      @hidden="onApplyModalHidden"
    >
      <template v-if="!syncState.started">
        <p class="font-weight-bold mb-3">
          Bundle này đang có {{ activeOrders.totalUsers }} user. Bạn có muốn áp dụng thay đổi cho các user này không?
        </p>
        <h6 class="mb-2">Tóm tắt thay đổi</h6>
        <div style="max-height: 320px; overflow-y: auto">
          <table class="table table-sm table-bordered mb-0">
            <thead class="thead-light">
              <tr>
                <th style="width: 30%">Mục</th>
                <th style="width: 35%">Trước</th>
                <th style="width: 35%">Sau</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in changes" :key="idx">
                <td>{{ row.label }}</td>
                <td class="text-muted">{{ row.before }}</td>
                <td class="font-weight-bold">{{ row.after }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-end flex-wrap mt-4">
          <b-button variant="secondary" class="mr-2 mb-2" @click="$bvModal.hide('modal-apply-bundle-changes')">
            Hủy
          </b-button>
          <b-button variant="outline-success" class="mr-2 mb-2" :disabled="loading" @click="applyNewUsersOnly">
            Chỉ áp dụng cho user mới
          </b-button>
          <b-button variant="success" class="mb-2" :disabled="loading" @click="applyAllUsers">
            Áp dụng cho tất cả user
          </b-button>
        </div>
      </template>

      <template v-else>
        <h6 class="mb-3 d-flex align-items-center">
          <b-spinner v-if="syncState.running" small variant="success" class="mr-2" />
          <span v-else class="text-success mr-2">&#10003;</span>
          {{ syncState.running ? 'Đang cập nhật bundle cho user...' : 'Đã cập nhật xong' }}
        </h6>
        <b-progress :max="syncState.total || 1" height="1.2rem" show-value>
          <b-progress-bar :value="syncState.processed" :animated="syncState.running" variant="success" />
        </b-progress>
        <b-row class="mt-3 text-center">
          <b-col>
            <small class="text-muted d-block">Đã xử lý</small>
            <span class="h5">{{ syncState.processed }}/{{ syncState.total }}</span>
          </b-col>
          <b-col>
            <small class="text-muted d-block">Thành công</small>
            <span class="h5 text-success">{{ syncState.success }}</span>
          </b-col>
          <b-col>
            <small class="text-muted d-block">Một phần</small>
            <span class="h5 text-warning">{{ syncState.partialRows.length }}</span>
          </b-col>
          <b-col>
            <small class="text-muted d-block">Thất bại</small>
            <span class="h5" :class="syncState.failedRows.length ? 'text-danger' : ''">{{ syncState.failedRows.length }}</span>
          </b-col>
        </b-row>
        <div v-if="syncState.failedRows.length || syncState.partialRows.length" class="mt-3" style="max-height: 220px; overflow-y: auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Số điện thoại</th>
                <th>Họ tên</th>
                <th>Kết quả</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in syncState.failedRows" :key="`f${idx}`">
                <td>{{ row.phoneNumber }}</td>
                <td>{{ row.name }}</td>
                <td class="text-danger">{{ row.message }}</td>
              </tr>
              <tr v-for="(row, idx) in syncState.partialRows" :key="`p${idx}`">
                <td>{{ row.phoneNumber }}</td>
                <td>{{ row.name }}</td>
                <td class="text-warning">{{ row.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="syncState.running" class="text-warning mt-3 mb-0">
          Vui lòng giữ nguyên trang cho tới khi cập nhật xong.
        </p>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="secondary" :disabled="syncState.running" @click="$bvModal.hide('modal-apply-bundle-changes')">
            Đóng
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import PackageManagementForm from './modal/PackageManagementForm.vue';

const SYNC_CONCURRENCY = 3;
const STATUS_TEXT = { 0: 'Nháp', 1: 'Hoạt động', 2: 'Ngưng hoạt động' };

const newSyncState = () => ({
  started: false,
  running: false,
  total: 0,
  processed: 0,
  success: 0,
  partialRows: [],
  failedRows: [],
});

export default {
  name: 'PackageManagementEdit',
  components: {
    'package-management-form': PackageManagementForm,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    form_type: {
      type: String,
      default: 'edit',
    },
    itemData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        name: '',
        description: '',
        originalPrice: '',
        currentPrice: '',
        status: 0,
        months: '',
        medicineItems: [],
        servicePackageItems: [],
        serviceItems: [],
      },
      originalForm: null,
      activeOrders: { totalUsers: 0, orders: [] },
      changes: [],
      syncState: newSyncState(),
    };
  },

  computed: {
    isCreateForm() {
      return !this.id;
    },
  },

  created() {
    if (this.itemData) {
      this.mapItemDataToForm(this.itemData);
    } else {
      this.loadData();
    }
  },

  methods: {
    mapItemDataToForm(data) {
      const rawItems = data.items || [];
      const med = rawItems.filter((i) => i.itemType === 0);
      const pkg = rawItems.filter((i) => i.itemType === 1);
      const svc = rawItems.filter((i) => i.itemType === 2);

      this.form = {
        id: data.id || null,
        name: data.name || '',
        description: data.description || '',
        originalPrice: data.price ?? 0,
        currentPrice: data.salePrice ?? 0,
        status: data.status !== undefined ? data.status : 0,
        months: data.duration ?? 6,
        medicineItems: med.map((x) => ({
          medicineId: x.itemId,
          quantity: x.quantity || 1,
          isUnlimited: x.isUnlimited || 0,
          discountValue: x.discountValue || 0,
          discountType: x.discountType || 0,
        })),
        servicePackageItems: pkg.map((x) => ({
          servicePackageId: x.itemId,
          isUnlimited: x.isUnlimited || 0,
          discountValue: x.discountValue || 0,
          discountType: x.discountType || 0,
        })),
        serviceItems: svc.map((x) => ({
          serviceId: x.itemId,
          quantity: x.quantity || 1,
          isUnlimited: x.isUnlimited || 0,
          discountValue: x.discountValue || 0,
          discountType: x.discountType || 0,
        })),
      };
      this.originalForm = JSON.parse(JSON.stringify(this.form));
    },

    async loadData() {
      if (!this.id) return;
      try {
        this.loading = true;
        const { data } = await this.$api.get(`SalePackage/${this.id}`);
        this.mapItemDataToForm(data);
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi!', msg: error });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    async handleValidate() {
      let isValid = true;
      try {
        isValid = await this.$refs.packageForm.validate();
      } catch (e) {
        console.error('Validate error:', e);
      }
      if (!isValid) {
        this.$toastr.e({ title: 'Lỗi!', msg: 'Vui lòng điền đầy đủ thông tin bắt buộc!' });
        return;
      }

      if (this.isCreateForm) {
        if (await this.savePackage()) {
          this.$toastr.s({ title: 'Thành công!', msg: 'Thêm mới gói dịch vụ thành công!' });
          this.goBack();
        }
        return;
      }

      this.changes = this.buildChanges();
      if (!this.changes.length) {
        await this.saveAndLeave();
        return;
      }

      try {
        this.loading = true;
        const { data } = await this.$api.get(`UserBundleOrders/active-by-sale-package/${this.form.id}`);
        this.activeOrders = { totalUsers: data?.totalUsers || 0, orders: data?.orders || [] };
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi!', msg: error?.message || 'Không kiểm tra được user đang dùng bundle' });
        return;
      } finally {
        this.loading = false;
      }

      if (!this.activeOrders.orders.length) {
        await this.saveAndLeave();
        return;
      }
      this.syncState = newSyncState();
      this.$bvModal.show('modal-apply-bundle-changes');
    },

    buildPayload() {
      const items = [];

      const medicineList = this.form.medicineItems || [];
      if (medicineList.length > 0) {
        items.push({
          itemType: 0,
          itemList: medicineList.map((m) => ({
            id: m.medicineId,
            count: m.quantity || 1,
            isUnlimited: m.isUnlimited || 0,
            discountValue: m.discountValue || 0,
            discountType: m.discountType || 0,
          })),
        });
      }

      const servicePackageList = this.form.servicePackageItems || [];
      if (servicePackageList.length > 0) {
        items.push({
          itemType: 1,
          itemList: servicePackageList.map((p) => ({
            id: p.servicePackageId,
            count: 1,
            isUnlimited: p.isUnlimited || 0,
            discountValue: p.discountValue || 0,
            discountType: p.discountType || 0,
          })),
        });
      }

      const serviceList = this.form.serviceItems || [];
      if (serviceList.length > 0) {
        items.push({
          itemType: 2,
          itemList: serviceList.map((s) => ({
            id: s.serviceId,
            count: s.quantity || 1,
            isUnlimited: s.isUnlimited || 0,
            discountValue: s.discountValue || 0,
            discountType: s.discountType || 0,
          })),
        });
      }

      const payload = new FormData();
      payload.append('name', this.form.name || '');
      payload.append('duration', parseInt(this.form.months) || 6);
      payload.append('price', parseInt(this.form.originalPrice) || 0);
      payload.append('salePrice', parseInt(this.form.currentPrice) || 0);
      payload.append('description', this.form.description || '');
      payload.append('status', parseInt(this.form.status) || 0);
      payload.append('items', items.length > 0 ? JSON.stringify(items) : '');
      return payload;
    },

    async savePackage() {
      const payload = this.buildPayload();
      try {
        this.loading = true;
        if (this.isCreateForm) {
          await this.$api.post('SalePackage', payload);
        } else {
          payload.append('id', this.form.id);
          await this.$api.put(`SalePackage/${this.form.id}`, payload);
        }
        return true;
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi!', msg: error?.message || error });
        return false;
      } finally {
        this.loading = false;
      }
    },

    async saveAndLeave() {
      if (await this.savePackage()) {
        this.$toastr.s({ title: 'Thành công!', msg: 'Cập nhật gói dịch vụ thành công!' });
        this.goBack();
      }
    },

    async applyNewUsersOnly() {
      if (!(await this.savePackage())) return;
      this.$bvModal.hide('modal-apply-bundle-changes');
      this.$toastr.s({
        title: 'Thành công!',
        msg: 'Đã lưu bundle. User hiện có giữ nguyên phiên bản cũ, user được gán sau sẽ nhận phiên bản mới.',
      });
      this.goBack();
    },

    async applyAllUsers() {
      if (!(await this.savePackage())) return;

      const queue = [...this.activeOrders.orders];
      this.syncState = { ...newSyncState(), started: true, running: true, total: queue.length };
      window.addEventListener('beforeunload', this.warnBeforeUnload);

      const worker = async () => {
        while (queue.length) {
          const order = queue.shift();
          await this.syncOrder(order);
          this.syncState.processed += 1;
        }
      };
      try {
        await Promise.all(Array.from({ length: Math.min(SYNC_CONCURRENCY, queue.length) }, worker));
      } finally {
        this.syncState.running = false;
        window.removeEventListener('beforeunload', this.warnBeforeUnload);
      }
    },

    async syncOrder(order) {
      const row = { phoneNumber: order.phoneNumber, name: order.name };
      try {
        const { data } = await this.$api.post(`UserBundleOrders/${order.orderId}/sync-sale-package`, {});
        if (data?.partial) {
          this.syncState.partialRows.push({ ...row, message: data.message || 'Cập nhật một phần' });
        } else {
          this.syncState.success += 1;
        }
      } catch (error) {
        this.syncState.failedRows.push({ ...row, message: error?.message || 'Không cập nhật được' });
      }
    },

    onApplyModalHidden() {
      if (this.syncState.started && !this.syncState.running) {
        this.$toastr.s({
          title: 'Hoàn tất',
          msg: `Cập nhật thành công ${this.syncState.success + this.syncState.partialRows.length}/${this.syncState.total} user, thất bại ${this.syncState.failedRows.length}.`,
        });
        this.goBack();
      }
    },

    warnBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = '';
    },

    catalogNames() {
      const formRef = this.$refs.packageForm || {};
      const toMap = (list) => (list || []).reduce((acc, x) => {
        acc[x.id] = x.name || x.shortName || x.id;
        return acc;
      }, {});
      return {
        0: toMap(formRef.medicines),
        1: toMap(formRef.servicePackages),
        2: toMap(formRef.services),
      };
    },

    itemsOf(form) {
      const list = [];
      (form.medicineItems || []).forEach((x) => list.push({ type: 0, id: x.medicineId, quantity: x.quantity || 1, isUnlimited: x.isUnlimited || 0, discountValue: x.discountValue || 0, discountType: x.discountType || 0 }));
      (form.servicePackageItems || []).forEach((x) => list.push({ type: 1, id: x.servicePackageId, quantity: 1, isUnlimited: x.isUnlimited || 0, discountValue: x.discountValue || 0, discountType: x.discountType || 0 }));
      (form.serviceItems || []).forEach((x) => list.push({ type: 2, id: x.serviceId, quantity: x.quantity || 1, isUnlimited: x.isUnlimited || 0, discountValue: x.discountValue || 0, discountType: x.discountType || 0 }));
      return list;
    },

    describeItem(item) {
      const qty = item.isUnlimited ? 'Không giới hạn' : `SL ${item.quantity}`;
      const discount = item.discountValue ? `, giảm ${item.discountValue}${item.discountType === 1 ? '%' : 'đ'}` : '';
      return `${qty}${discount}`;
    },

    buildChanges() {
      const before = this.originalForm;
      const after = this.form;
      if (!before) return [];
      const rows = [];
      const money = (v) => Number(v || 0).toLocaleString('vi-VN');
      const fields = [
        ['Tên gói', before.name, after.name],
        ['Mô tả', before.description, after.description],
        ['Giá gốc', money(before.originalPrice), money(after.originalPrice)],
        ['Giá bán', money(before.currentPrice), money(after.currentPrice)],
        ['Thời hạn', `${before.months} tháng`, `${after.months} tháng`],
        ['Trạng thái', STATUS_TEXT[before.status], STATUS_TEXT[after.status]],
      ];
      fields.forEach(([label, b, a]) => {
        if (String(b ?? '') !== String(a ?? '')) rows.push({ label, before: b || '—', after: a || '—' });
      });

      const names = this.catalogNames();
      const typeLabel = { 0: 'Thuốc/TPCN', 1: 'Gói dịch vụ', 2: 'Dịch vụ' };
      const key = (x) => `${x.type}:${x.id}`;
      const beforeItems = this.itemsOf(before);
      const afterItems = this.itemsOf(after);
      const beforeMap = Object.fromEntries(beforeItems.map((x) => [key(x), x]));
      const afterMap = Object.fromEntries(afterItems.map((x) => [key(x), x]));
      const nameOf = (x) => `${typeLabel[x.type]}: ${names[x.type][x.id] || x.id}`;

      afterItems.forEach((x) => {
        const old = beforeMap[key(x)];
        if (!old) {
          rows.push({ label: nameOf(x), before: 'Chưa có', after: `Thêm mới (${this.describeItem(x)})` });
        } else if (this.describeItem(old) !== this.describeItem(x)) {
          rows.push({ label: nameOf(x), before: this.describeItem(old), after: this.describeItem(x) });
        }
      });
      beforeItems.forEach((x) => {
        if (!afterMap[key(x)]) rows.push({ label: nameOf(x), before: this.describeItem(x), after: 'Bỏ khỏi gói' });
      });
      return rows;
    },

    goBack() {
      this.$router.push({ name: 'package_management' });
    },
  },
};
</script>

<style scoped lang="scss">
.package-management-edit-page {
  background-color: #f8f9fa;
  min-height: 100vh;
}
</style>
